import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TerroirPage = () => {
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/terroir`);
      setPageContent(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1915]">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(./images/terroir.webp)',
            filter: 'brightness(0.5)'
          }}
        />
        <div className="relative z-10 text-center px-6">
          <h1 className="playfair text-5xl lg:text-7xl font-bold text-white mb-6" data-testid="terroir-page-title">
            Our Terroir & Olive Grove
          </h1>
          <div className="w-32 h-1 bg-[#C9A961] mx-auto"></div>
        </div>
      </section>

      {/* Soul of Legato d'Oro */}
      <section className="py-24 px-6 bg-[#221F1A] stone-texture">
        <div className="max-w-6xl mx-auto">
          <h2 className="playfair text-4xl lg:text-5xl font-bold text-[#C9A961] text-center mb-16">
            The Soul of Legato d'Oro
          </h2>

          <div className="space-y-20">
            {/* Our Land, Our Promise */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="playfair text-3xl font-semibold text-[#C9A961] mb-6">
                  Our Land, Our Promise
                </h3>
                <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                  <p>
                    Our olive grove was born under the Mediterranean sun. More than just
                    a cultivation site, it's a heritage, a sanctuary where nature sets the rhythm.
                  </p>
                  <p>
                    Each tree tells a story. Our unique terroir imbues the oil with exceptional
                    character—a subtle balance between strength and finesse.
                  </p>
                  <p>
                    Visiting our estate is a sensory journey into Mediterranean authenticity,
                    where every detail reflects our commitment to excellence.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="./images/soul.webp"
                  alt="Ancient olive tree"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* Pillars of Excellence */}
            <div>
              <h3 className="playfair text-3xl font-semibold text-[#C9A961] text-center mb-8">
                Pillars of Excellence
              </h3>
              <div className="flex justify-center">
                <img
                  src="./images/excellence.webp"
                  alt="Pillars of Excellence"
                  className="max-w-4xl w-full rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* Traditional Cuvée Secret */}
            <div className="bg-[#2A2720] rounded-lg p-12 border-2 border-[#C9A961]/30">
              <h3 className="playfair text-3xl font-semibold text-[#C9A961] text-center mb-8">
                The Secret of Our Traditional Cuvée
              </h3>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img
                    src="./images/secret.webp"
                    alt="Legato d'Oro bottle with olives and bread"
                    className="rounded-lg shadow-xl mx-auto"
                  />
                </div>
                <div className="space-y-4 text-gray-300 text-lg">
                  <p>
                    Be enchanted by this exceptional oil, the quintessence of our terroir.
                    A <span className="text-[#C9A961] font-semibold">golden robe with emerald green reflections</span>.
                  </p>
                  <p>
                    Rich aromatic profile: <span className="text-[#C9A961] font-semibold">intense green fruity notes</span> combined
                    with delicate <span className="text-[#C9A961] font-semibold">fresh almond nuances</span>.
                  </p>
                  <p>
                    An unforgettable gustatory experience that brings the golden harmony
                    from our land to your table—a subtle link that enhances every dish.
                  </p>
                  <div className="flex space-x-4 pt-4">
                    <div className="text-center">
                      <div className="text-2xl mb-1"><img
                    src="./images/fresh-green.png"
                    alt="Leaf icon representing fresh green notes"
                    className="shadow-xl mx-auto"
                  /></div>
                      <p className="text-xs text-gray-500">Fresh Green</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl mb-1"><img
                    src="./images/almond.png"
                    alt="Almond icon representing Almond notes"
                    className="shadow-xl mx-auto"
                  /></div>
                      <p className="text-xs text-gray-500">Almond Notes</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl mb-1"><img
                    src="./images/premium.png"
                    alt="Premium icon representing premium quality"
                    className="shadow-xl mx-auto"
                  /></div>
                      <p className="text-xs text-gray-500">Premium</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Eco-Responsible Commitment */}
            <div className="text-center">
              <h3 className="playfair text-3xl font-semibold text-[#C9A961] mb-6">
                Our Eco-Responsible Commitment
              </h3>
              <div className="max-w-3xl mx-auto space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  We are conscious of our ecosystem's fragility. Our cultivation respects
                  biodiversity and we are committed to sustainable development to preserve
                  our terroir's richness for future generations.
                </p>
                <p>
                  Each drop is the result of our sincere commitment to nature-respecting
                  agriculture, ensuring that the land that has given us so much continues
                  to thrive for centuries to come.
                </p>
              </div>
              <div className="flex justify-center space-x-12 mt-12">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-[#C9A961]/20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-[#C9A961]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-white font-semibold">Sustainable</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-[#C9A961]/20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-[#C9A961]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <p className="text-white font-semibold">Biodiversity</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-[#C9A961]/20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-[#C9A961]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <p className="text-white font-semibold">Heritage</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TerroirPage;